import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonService } from '../../services/common.service';
import { GlobalService } from '../../services/global.service';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';
import { startWith } from 'rxjs/internal/operators/startWith';
import { UserSystemPreference } from '../../models/userSystemPreference';
import moment from 'moment';
import { DynamicDateAdapter } from '../../common/dynamic-date-adapter';
import { DateAdapter } from '@angular/material/core';
import { UpsertEmailDelegationRequest } from '../../models/substitution.models';
import { SubstitutionService } from '../../components/substitution/substitution.service';
import { DatePipe } from '@angular/common';
import { DateService } from '../../services/date.service';

@Component({
  selector: 'app-user-substitution',
  templateUrl: './user-substitution.component.html',
  styleUrl: './user-substitution.component.scss',
  providers: [DatePipe],
})

export class UserSubstitutionComponent {
  substituteForm!: FormGroup;
  substituteUserControl = new FormControl('', [Validators.required, this.substituteUserValidator.bind(this)]);
  users: any[] = [];
  filteredUsers: Observable<any[]> = new Observable();
  userPreferedDateformat!: string | 'dd/MM/yyyy';
  userSystemPreference: UserSystemPreference[] = [];
  message: string = '';
  isSuccess: boolean = false;

  constructor(
    private readonly substitutionService: SubstitutionService,
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly commonService: CommonService,
    private readonly dialogRef: MatDialogRef<UserSubstitutionComponent>,
    private readonly globalVariable: GlobalService,
    private readonly dateService: DateService
  ) {}

  ngOnInit(): void {
    this.substituteForm = this.fb.group({
      substituteUser: this.substituteUserControl,
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      comments: ['', Validators.required],
    });
        
    this.userSystemPreference = this.globalVariable.getUserPreferences;
    this.userSystemPreference.forEach((element) => {
      if (element.preferenceTitle == 'Date Format Display') {
        this.userPreferedDateformat = element.dateFormatOptions[0].defaultFormat.split(' ')[0];
      }
    });

    this.getUserDetails();
    this.onDatepickerOpened();
    this.loadData();

    this.filteredUsers = this.substituteUserControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || '')),
    );
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.users.filter((user) =>
      user.fullName.toLowerCase().includes(filterValue),
    );
  }

  onUserSelect(event: any): void {
    const selectedUser = this.users.find(
      (user) => user.emailId === event.option.value,
    );
    if (selectedUser) {
      this.substituteForm.patchValue({
        substituteUser: selectedUser.emailId.toLowerCase(),
      });
    }
  }

  private substituteUserValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const isValidUser = this.users.some(
      (user) => user.emailId === value,
    );

    return isValidUser ? null : { invalidSubstituteUser: true };
  }

  getUserDetails(): void {
    this.users = [];
    const currentUserEmail = this.globalVariable.getUserEmail?.toLowerCase();

    this.commonService.getReAssignUsers(this.globalVariable.getUserGPID, this.globalVariable.getSelectedSystemId)
      .subscribe({
        next: (response) => {
          response.forEach((item: any) => {
            if (item.emailId?.toLowerCase() === currentUserEmail) {
              return;
            }
            this.users.push({
              userId: item.userId,
              userName: item.userName,
              emailId: item.emailId.toLowerCase(),
              fullName: item.fullName,
            });
          });
        },
        error: (error) => {
          console.error('Error fetching users:', error);
        },
      }
    );
  }

  loadData(): void {
    this.substitutionService
      .getEmailDelegation({
        systemId: Number(this.globalVariable.getSelectedSystemId),
        userGpid: this.globalVariable.getUserGPID,
        fromEmail: this.globalVariable.getUserEmail.toLowerCase(),
      })
      .subscribe({
        next: (response) => {
          const data = response[0];
          const startDate = new Date(data.startDate);
          const endDate = new Date(data.endDate);

          this.substituteForm.patchValue({
            substituteUser: data.toEmail,
            startDate: startDate,
            endDate: endDate,
            comments: data.comments,
            fromEmail: data.fromEmail,
            toEmail: data.toEmail,
            isActive: data.isActive,
          });

          this.substituteUserControl.setValue(data.toEmail);
        },
        error: (error) => {
          console.error('Error fetching email delegation:', error);
        },
      }
    );
  }

  onSubmit() {
    if (this.substituteForm.valid) {
      this.updateEmailDelegationDetails();
    }
  }

  updateEmailDelegationDetails(): void {

    const payload: UpsertEmailDelegationRequest = {
      systemId: Number(this.globalVariable.getSelectedSystemId),
      userGpid: this.globalVariable.getUserGPID,
      fromEmail: this.globalVariable.getUserEmail.toLowerCase(),
      toEmail: this.substituteForm.value.substituteUser,
      comments: this.substituteForm.value.comments,
      startDate: this.formatDate(this.substituteForm.value.startDate),
      endDate: this.formatDate(this.substituteForm.value.endDate, true),
      isActive: true,
      isUserSubstitution: true
    };

    this.substitutionService.upsertEmailDelegation(payload).subscribe({
      next: (response) => {
        if (response) {
          this.isSuccess = true;
          this.message = 'Details saved successfully!';
        } else {    
          this.isSuccess = false;
          this.message = 'Failed to save details. Please try again.';      
        }
      },
      error: (error) => {
        this.isSuccess = false;
        this.message = 'Failed to save details. Please try again.';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close('cancel');
  }

  formatDate(date: any, isEndDate: boolean = false): string {
    const m = moment(date);
    if (isEndDate) {
      m.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 0
      });
    }
    return m.format('YYYY-MM-DDTHH:mm:ss.SSS');
  }

  onDatepickerOpened(): void {
    let rawFormat = this.userPreferedDateformat;
    if (!rawFormat) return;
    rawFormat = this.dateService.cleanFormat(rawFormat);
    const momentFormat = this.dateService.toMomentFormat(rawFormat);
    if (this.dateAdapter instanceof DynamicDateAdapter) {
      this.dateAdapter.setDynamicFormat(momentFormat);
    }
  }
}
